import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, type components } from '@/shared/api'

export type CurrentUser = components['schemas']['CurrentUserResponse']
export type AuthResponse = components['schemas']['AuthResponse']
export type LibraryRole = 'owner' | 'editor' | 'viewer'

const RANK: Record<LibraryRole, number> = { viewer: 1, editor: 2, owner: 3 }

/**
 * Сессия (ТЗ 18.4): access-токен только в памяти, refresh-токен в httpOnly-cookie.
 * Обновление выполняется «в один проход»: параллельные запросы ждут один общий /auth/refresh.
 */
export const useSessionStore = defineStore('session', () => {
  const accessToken = ref<string | null>(null)
  const user = ref<CurrentUser | null>(null)
  const status = ref<'unknown' | 'authenticated' | 'guest'>('unknown')
  /** Политика требует 2FA, а она не настроена: открыт только экран безопасности. */
  const mfaSetupRequired = ref(false)
  /** Промежуточный токен второго шага входа (живёт 5 минут на сервере). */
  const mfaChallenge = ref<string | null>(null)

  let refreshing: Promise<string | null> | null = null
  /** Проверяли ли политику 2FA после входа: сервер сам вернёт 403, но лучше сразу. */
  let policyChecked = false

  const isAuthenticated = computed(() => status.value === 'authenticated')
  /** Запуск без сети: войден ли пользователь, ещё неизвестно. */
  const isUnverified = computed(() => status.value === 'unknown')
  const isAdmin = computed(() => user.value?.isInstanceAdmin === true)

  function signIn(auth: AuthResponse): void {
    accessToken.value = auth.accessToken
    user.value = auth.user
    status.value = 'authenticated'
    mfaChallenge.value = null
  }

  function signOut(): void {
    policyChecked = false
    accessToken.value = null
    user.value = null
    status.value = 'guest'
    mfaSetupRequired.value = false
    mfaChallenge.value = null
  }

  function refresh(): Promise<string | null> {
    refreshing ??= (async () => {
      try {
        const { data, response } = await api.POST('/api/v1/auth/refresh', { body: {} })
        if (!response.ok || data === undefined) {
          signOut()
          return null
        }
        signIn(data)
        return data.accessToken
      } catch {
        // Нет связи: сессию не сбрасываем. При запуске без сети статус остаётся
        // неизвестным, приложение открывает оболочку и проверит вход, когда связь вернётся.
        return null
      } finally {
        refreshing = null
      }
    })()
    return refreshing
  }

  /** Один раз при запуске: есть ли действующая сессия по cookie. */
  async function ensure(): Promise<void> {
    if (status.value === 'unknown') await refresh()
  }

  /**
   * Один раз после входа: если политика требует 2FA, а она не включена, интерфейс
   * сразу ведёт на экран безопасности (ТЗ 18.3, п. 3), а не после первого отказа.
   */
  async function checkMfaPolicy(): Promise<void> {
    if (policyChecked || status.value !== 'authenticated') return
    policyChecked = true
    try {
      const { data } = await api.GET('/api/v1/me/2fa')
      if (data !== undefined) mfaSetupRequired.value = data.requiredByPolicy && !data.enabled
    } catch {
      policyChecked = false
    }
  }

  async function logout(everywhere = false): Promise<void> {
    try {
      await api.POST(everywhere ? '/api/v1/auth/logout-all' : '/api/v1/auth/logout')
    } catch {
      // Сессия на сервере истечёт сама.
    }
    signOut()
  }

  function setUser(next: CurrentUser): void {
    user.value = next
  }

  /** Роль пользователя в библиотеке или null, если он не участник. */
  function roleIn(libraryId: string): LibraryRole | null {
    const membership = user.value?.libraries.find((l) => l.id === libraryId)
    return (membership?.role as LibraryRole | undefined) ?? null
  }

  function hasRole(libraryId: string, minimum: LibraryRole): boolean {
    const role = roleIn(libraryId)
    return role !== null && RANK[role] >= RANK[minimum]
  }

  /** Может ли пользователь что-то добавлять хотя бы в одну библиотеку. */
  const canEditSomewhere = computed(() =>
    (user.value?.libraries ?? []).some((l) => RANK[l.role as LibraryRole] >= RANK.editor),
  )

  return {
    accessToken,
    user,
    status,
    mfaSetupRequired,
    mfaChallenge,
    isAuthenticated,
    isUnverified,
    isAdmin,
    canEditSomewhere,
    signIn,
    signOut,
    refresh,
    ensure,
    checkMfaPolicy,
    logout,
    setUser,
    roleIn,
    hasRole,
  }
})
